import { on, unByKey } from '@disy/lib/event-util';
import { logger } from '@disy/lib/logging';
import { mediaPrefersReducedMotion } from '@disy/lib/media';

/**
 * Executes function when the animation/transition is finished or (if animations/transitions are disabled) immediately.
 *
 * If the animation-iteration-count is != 1, the callback is not called.
 *
 * @param element - An element that the animation is executed on
 * @param callback - Function to execute
 * @param timeout - Timeout in milliseconds to execute the callback if the animation/transition is not finished;
 *   if not provided, the timeout is calculated based on the CSS transition and animation properties.
 */
export function executeAfterAnimation(element: HTMLElement, callback: () => unknown, timeout?: number) {
  if (isAnimationEnabled()) {
    const computedStyle = getComputedStyle(element);
    if (computedStyle.animationIterationCount !== '1') {
      logger.warn('Only animation-iteration-count: 1 is currently supported. The callback will not be called.');
    }

    // When having multiple transitions listeners registered at the same time, on Chrome at least,
    // the transitionend event is not fired for the subsequent registrations sometimes. Instead
    // the transitioncancel event is fired (while the animation works just fine).
    //
    // In some cases the transition isn't executed at all. To guard against this case the timeout
    // parameter was introduced.
    timeout ??= getTimeout(computedStyle);

    const wrappedCallback = () => {
      clearTimeout(timeoutId);
      unByKey(eventKey);
      callback();
    };
    const timeoutId = setTimeout(wrappedCallback, timeout);
    const eventKey = on(
      element,
      ['transitionend', 'transitioncancel', 'animationend', 'animationcancel'],
      wrappedCallback,
    );
  } else {
    callback();
  }
}

function getTimeout(computedStyle: CSSStyleDeclaration) {
  const transitionTimeout =
    (parseFloat(computedStyle.transitionDelay) + parseFloat(computedStyle.transitionDuration)) * 1000;
  const animationTimeout =
    (parseFloat(computedStyle.animationDelay) + parseFloat(computedStyle.animationDuration)) * 1000;
  return Math.max(transitionTimeout, animationTimeout);
}

/**
 * Checks if the animations/transitions are enabled.
 * Animations and transitions can be disabled by setting prefers-reduced-motion CSS media feature.
 * see: https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-reduced-motion
 *
 * @return true is animations/transitions are NOT disabled
 */
export function isAnimationEnabled() {
  return !mediaPrefersReducedMotion.matches;
}
